/**
 * Landing CTA organism component
 * Main call-to-action section encouraging users to start using the application
 */

// #region Imports
import {
    Box,
    Typography,
    Container,
    useTheme,
    Button,
    Stack,
    Paper
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
    PlayArrow,
    Star,
    Speed,
    Security
} from "@mui/icons-material";
import { Link } from "@tanstack/react-router";
// #endregion

// #region Types
/**
 * Props for LandingCTA organism component
 */
interface LandingCTAOrganismProps {
    /** Optional test ID for testing purposes */
    "data-testid"?: string;
}
// #endregion

// #region Component
/**
 * LandingCTA organism component for the main call-to-action
 * Encourages users to start using the application with compelling messaging
 *
 * @param props - The component props
 * @returns JSX element representing the CTA section
 */
function LandingCTAOrganism({ ...props }: LandingCTAOrganismProps) {
    const { t } = useTranslation('landing');
    const theme = useTheme();

    return (
        <Box
            component="section"
            sx={{
                py: { xs: 8, md: 12 },
                backgroundColor: "background.paper",
                position: "relative",
                overflow: "hidden",
            }}
            {...props}
        >
            {/* Background Elements */}
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.secondary.main}08)`,
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    top: "20%",
                    right: "-10%",
                    width: 300,
                    height: 300,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${theme.palette.primary.main}15, transparent)`,
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    bottom: "20%",
                    left: "-10%",
                    width: 400,
                    height: 400,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${theme.palette.secondary.main}10, transparent)`,
                    pointerEvents: "none",
                }}
            />

            <Container maxWidth="md">
                <Paper
                    elevation={8}
                    sx={[
                        {
                            p: { xs: 4, md: 6 },
                            textAlign: "center",
                            position: "relative",
                            background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}50)`,
                            backdropFilter: "blur(20px)",
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 3,
                        },
                        (theme) =>
                            theme.applyStyles("dark", {
                                background: `linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.04))`,
                                border: `1px solid rgba(255, 255, 255, 0.12)`,
                            }),
                    ]}
                >
                    {/* Header */}
                    <Typography
                        variant="h2"
                        component="h2"
                        sx={[
                            {
                                mb: 3,
                                fontSize: { xs: "2rem", md: "2.5rem" },
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                                textAlign: "center",
                            },
                            (theme) =>
                                theme.applyStyles("dark", {
                                    color: "#FFFFFF",
                                }),
                        ]}
                    >
                        {t('cta.title')}
                    </Typography>

                    <Typography
                        variant="h6"
                        sx={[
                            {
                                mb: 4,
                                color: theme.palette.text.secondary,
                                lineHeight: 1.6,
                                maxWidth: 500,
                                mx: "auto",
                            },
                            (theme) =>
                                theme.applyStyles("dark", {
                                    color: "rgba(255, 255, 255, 0.8)",
                                }),
                        ]}
                    >
                        {t('cta.subtitle')}
                    </Typography>

                    {/* Features Bar */}
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        justifyContent="center"
                        sx={[
                            {
                                mb: 4,
                                "& > *": {
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: theme.palette.text.secondary,
                                    fontSize: "0.9rem",
                                    justifyContent: { xs: "center", sm: "flex-start" },
                                },
                            },
                            (theme) =>
                                theme.applyStyles("dark", {
                                    "& > *": {
                                        color: "rgba(255, 255, 255, 0.8)",
                                    },
                                }),
                        ]}
                        role="list"
                        aria-label="Key features"
                    >
                        <Box role="listitem">
                            <Star color="warning" fontSize="small" aria-hidden="true" />
                            <span>{t('cta.features.free')}</span>
                        </Box>
                        <Box role="listitem">
                            <Speed color="success" fontSize="small" aria-hidden="true" />
                            <span>{t('cta.features.instant')}</span>
                        </Box>
                        <Box role="listitem">
                            <Security color="primary" fontSize="small" aria-hidden="true" />
                            <span>{t('cta.features.noRegistration')}</span>
                        </Box>
                    </Stack>

                    {/* Main CTA Button */}
                    <Button
                        id="cta-section-generate"
                        component={Link}
                        to="/generate"
                        variant="contained"
                        size="large"
                        startIcon={<PlayArrow aria-hidden="true" />}
                        aria-label="Start optimizing your cutting layouts now"
                        sx={[
                            {
                                fontSize: "1.2rem",
                                fontWeight: 600,
                                py: 2,
                                px: 5,
                                borderRadius: 2,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                color: "rgba(255, 255, 255, 0.9)",
                                boxShadow: theme.shadows[8],
                                "&:hover": {
                                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                    transform: "translateY(-2px)",
                                    boxShadow: theme.shadows[12],
                                },
                                "&:focus": {
                                    outline: `3px solid ${theme.palette.primary.light}`,
                                    outlineOffset: 2,
                                },
                                transition: "all 0.3s ease",
                                mb: 3,
                            },
                            (theme) =>
                                theme.applyStyles("dark", {
                                    background: `linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.8))`,
                                    color: "#000000",
                                    "&:hover": {
                                        background: `linear-gradient(135deg, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9))`,
                                    },
                                    "&:focus": {
                                        outline: `3px solid rgba(255, 255, 255, 0.5)`,
                                    },
                                }),
                        ]}
                        data-testid="start-optimizing-cta-button"
                    >
                        {t('cta.button.primary')}
                    </Button>

                    {/* Subtext */}
                    <Typography
                        variant="body2"
                        sx={[
                            {
                                color: theme.palette.text.secondary,
                                fontStyle: "italic",
                                mb: 2,
                            },
                            (theme) =>
                                theme.applyStyles("dark", {
                                    color: "rgba(255, 255, 255, 0.7)",
                                }),
                        ]}
                    >
                        {t('cta.button.subtitle')}
                    </Typography>

                    {/* Social Proof */}
                    <Box sx={[
                        {
                            mt: 4,
                            pt: 3,
                            borderTop: `1px solid ${theme.palette.divider}`
                        },
                        (theme) =>
                            theme.applyStyles("dark", {
                                borderTop: `1px solid rgba(255, 255, 255, 0.12)`,
                            }),
                    ]}>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={4}
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Box sx={{ textAlign: "center" }}>
                                <Typography
                                    variant="h4"
                                    sx={[
                                        {
                                            color: theme.palette.primary.main,
                                            fontWeight: 700,
                                            mb: 0.5,
                                        },
                                        (theme) =>
                                            theme.applyStyles("dark", {
                                                color: "rgba(255, 255, 255, 0.8)",
                                            }),
                                    ]}
                                >
                                    30%
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={[
                                        {
                                            color: theme.palette.text.secondary,
                                        },
                                        (theme) =>
                                            theme.applyStyles("dark", {
                                                color: "rgba(255, 255, 255, 0.8)",
                                            }),
                                    ]}
                                >
                                    {t('cta.socialProof.wasteReduction')}
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: "center" }}>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: theme.palette.secondary.main,
                                        fontWeight: 700,
                                        mb: 0.5,
                                    }}
                                >
                                    10x
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={[
                                        {
                                            color: theme.palette.text.secondary,
                                        },
                                        (theme) =>
                                            theme.applyStyles("dark", {
                                                color: "rgba(255, 255, 255, 0.8)",
                                            }),
                                    ]}
                                >
                                    {t('cta.socialProof.fasterPlanning')}
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: "center" }}>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: theme.palette.success.main,
                                        fontWeight: 700,
                                        mb: 0.5,
                                    }}
                                >
                                    100%
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={[
                                        {
                                            color: theme.palette.text.secondary,
                                        },
                                        (theme) =>
                                            theme.applyStyles("dark", {
                                                color: "rgba(255, 255, 255, 0.8)",
                                            }),
                                    ]}
                                >
                                    {t('cta.socialProof.freeForever')}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
}
// #endregion

// #region Exports
export default LandingCTAOrganism;
export type { LandingCTAOrganismProps };
// #endregion
